import React, { useState } from 'react'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { useQuery } from '@apollo/client'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { PageHeader } from 'components/page-header'
import RouterLinkButton from 'components/router-link-button'
import EmptyPageState from 'components/empty-page-state'
import GET_GROUP_EVENTS from './query'
import EventsDisplay from './events-display'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from 'components/error'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Switch from '@material-ui/core/Switch'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
import { parseEvents } from 'utils/eventUtils'
import PartyPopperIcon from 'components/icons/party-popper-icon'
import { makeStyles } from '@material-ui/core/styles'
import { ADMTrainingExternalLink } from 'lib/pga-dot-coach'
import { red } from '@material-ui/core/colors'

const useStyles = makeStyles(theme => ({
  tabWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '24px',
    paddingBottom: '0px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      padding: 0,
    },
  },
  showCancellations: {
    [theme.breakpoints.down('xs')]: {
      marginTop: '20px',
    },
  },
  containerStyles: {
    [theme.breakpoints.down('xs')]: {
      padding: '0',
      marginBottom: '60px',
    },
  },
}))

const EventsPageData = ({ selectedTab, showCanceledEvents }) => {
  const history = useHistory()

  const { data, loading, error, refetch } = useQuery(GET_GROUP_EVENTS, {
    variables: {
      orderBy: { field: 'START_DATE_TIME', direction: 'ASC' },
      limit: null,
    },
  })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error />

  const events = get(data, 'currentUser.groupEvents', [])

  const groupEvents = showCanceledEvents
    ? parseEvents(events)
    : parseEvents(events).filter(e => !e.isCanceled)

  const upcomingEvents = groupEvents.filter(
    b =>
      DateTime.fromISO(b.seriesEndDateTime) >= DateTime.local().startOf('day'),
  )
  const pastEvents = groupEvents.filter(
    b =>
      DateTime.fromISO(b.seriesEndDateTime) < DateTime.local().startOf('day'),
  )

  const currentlyViewingEvents =
    selectedTab === 'upcoming' ? upcomingEvents : pastEvents

  const { canPromoteContent } = data.currentUser.attributes

  return (
    <>
      {!canPromoteContent && (
        <Container maxWidth="md">
          <Box mt={4} p={2} borderRadius={4} bgcolor={red[50]}>
            <Typography variant="body2" align="center">
              {
                'You are currently ineligible to promote your events on PGA.com. Please complete '
              }
              <ADMTrainingExternalLink />
              {' to become eligible.'}
            </Typography>
          </Box>
        </Container>
      )}
      {currentlyViewingEvents.length ? (
        <EventsDisplay events={currentlyViewingEvents} refetch={refetch} />
      ) : (
        <EmptyPageState
          actionFn={() => history.push('/pga-coach/bookings/events/new')}
          IconComp={() => <PartyPopperIcon />}
          title={`No ${
            selectedTab === 'upcoming' ? 'Upcoming' : 'Past'
          } Events`}
          caption="Events represent your group coach offerings. Examples include Whiskey and Wedges, Wine and Nine, Night Glow Golf, etc."
          buttonText="Create Event"
        />
      )}
    </>
  )
}

const EventsPage = () => {
  const [selectedTab, setSelectedTab] = useState('upcoming')
  const [showCanceledEvents, setShowCanceledEvents] = useState(false)
  const classes = useStyles()
  const { path } = useRouteMatch()

  const crumbs = [
    {
      label: 'Home',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
    {
      label: 'Schedule',
      to: [...path.split('/').slice(0, 3), 'offerings'].join('/'),
    },
  ]
  return (
    <>
      <PageHeader
        title="Events"
        crumbs={crumbs}
        actions={
          <RouterLinkButton
            variant="contained"
            color="primary"
            to="/pga-coach/bookings/events/new"
          >
            Create Event
          </RouterLinkButton>
        }
        tabs={
          <Container className={classes.containerStyles}>
            <Box className={classes.tabWrapper}>
              <Tabs
                value={selectedTab}
                onChange={(_, newValue) => setSelectedTab(newValue)}
              >
                <Tab label="Upcoming" value="upcoming" />
                <Tab label="Past" value="past" />
              </Tabs>
              <Box>
                <FormControlLabel
                  className={classes.showCancellations}
                  size="size"
                  control={
                    <Switch
                      checked={showCanceledEvents}
                      onChange={e => setShowCanceledEvents(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="button">Show Cancellations</Typography>
                  }
                />
              </Box>
            </Box>
          </Container>
        }
        fixedMobileActions
      />
      <EventsPageData
        selectedTab={selectedTab}
        showCanceledEvents={showCanceledEvents}
      />
    </>
  )
}

export default EventsPage
